import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { debounceTime, defer, distinctUntilChanged, from, map, Observable, Subject } from 'rxjs';

interface TaskSettingInput {
    description: string;
    label: string;
    value: any;
    inputChanged: Subject<number>
}

interface TaskSetting {
    id: string;
    name: string;
    title: string;
    subheader: string;
    description: string;
    icon: string;
    disabled: boolean;
    enabled: boolean;
    input?: TaskSettingInput;
    order: number;
    // Don't log as error, it maps this way from the API.
    // eslint-disable-next-line camelcase
    task_type: number;
}

@Component({
    selector: 'sfx-task-settings',
    templateUrl: './task-settings.component.html',
    styleUrls: ['./task-settings.component.scss']
})
export class TaskSettingsComponent implements OnInit {

    public settings$!: Observable<TaskSetting[]>;
    private settingTranslations: { [key: string]: string };

    constructor(
        @Inject('tasksService') private tasksService: any,
        @Inject('teamService') private teamService: any,
        @Inject('modelService') private modelService: any,
        private translate: TranslateService
    ) {}

    public ngOnInit(): void {

        this.settingTranslations = {
            1: this.translate.instant('At the end of a meeting, Salesflare creates a task to add minutes to this meeting. When you add minutes, the task automatically completes.'),
            2: this.translate.instant('When an email comes in from a customer, Salesflare automatically creates a task to remind you to answer. When you email back, the task automatically completes. (This happens when the email appears in the timeline and may take a few minutes.)'),
            3: this.translate.instant('When an account with an opportunity is inactive for x days, Salesflare creates a task to follow up. When you send an email, the task automatically completes. (This happens when the email appears in the timeline and may take a few minutes.)'),
            5: this.translate.instant('When you email or plan a meeting with a new contact on an account, Salesflare suggests you to add the contact. When you add the contact, the task automatically completes.'),
            6: this.translate.instant('When you are in contact with people of a certain company, Salesflare suggests you to add the company as an account. When you add the account, the task automatically completes.'),
            7: this.translate.instant('A meeting can be shown in tasks')
        };

        this.settings$ = defer(() => from(this.tasksService.getSettings()))
            .pipe(
                map((response: any) => response.data),
                map((settings: any) => {
                    return settings.filter((setting: any) => {
                        // Remove the suggested_contact and suggested_account settings from task settings screen for now
                        // Since they are not being planned in the nearby future.
                        if (setting.name !== 'suggested_contact' && setting.name !== 'suggested_account') {
                            return true;
                        }

                        return false;
                    });
                }),
                map<any, TaskSetting[]>((settings: any) => {

                    return settings.map((setting: any) => {

                        setting.enabled = !setting.disabled;

                        setting.description = this.settingTranslations[setting.task_type] || setting.description;

                        switch (setting.name) {
                            case 'meeting_notes':
                                setting.title = this.translate.instant('Meeting notes');
                                setting.subheader = '';
                                setting.icon = 'meeting-live';
                                setting.order = 1;
                                break;
                            case 'email_reply':
                                setting.title = this.translate.instant('Email needing a reply');
                                setting.subheader = '';
                                setting.icon = 'email';
                                setting.order = 2;
                                break;
                            case 'account_inactive':
                                setting.title = this.translate.instant('Account inactive for X days');
                                setting.subheader = '';
                                setting.icon = 'action-needed';
                                setting.order = 3;

                                if (this.modelService.me.is_admin) {
                                    setting.input = {
                                        description: this.translate.instant('Set the default # days to wait before an account becomes inactive for your team below. \n You can do this more in depth for each stage in the Configure Pipelines setting.'),
                                        label: this.translate.instant('# days'),
                                        value: this.modelService.me.team.days_until_account_inactive,
                                        inputChanged: new Subject<number>().pipe(
                                            debounceTime(750),
                                            distinctUntilChanged()
                                        )
                                    };

                                    setting.input.inputChanged.subscribe((value: any) => {

                                        if (value !== null) {
                                            return this.teamService.update({ days_until_account_inactive: value }).then(() => {

                                                // We update the model here to ensure the user always sees the right value here (when leaving task settings and then coming back) so the user doesn't have to wait until the next /me call
                                                this.modelService.me.team.days_until_account_inactive = value;
                                            });
                                        }
                                    });
                                }

                                break;
                            // Case 'suggested_contact':
                            //     setting.title = 'Suggested contacts';
                            //     setting.subheader = '';
                            //     setting.icon = 'person';
                            //     break;
                            // case 'suggested_account':
                            //     setting.title = 'Suggested account';
                            //     setting.subheader = '';
                            //     setting.icon = 'accounts';
                            //     break;
                        }

                        return setting;
                    });
                }),
                map<TaskSetting[], TaskSetting[]>((settings: TaskSetting[]) => {
                    return settings.sort((a: TaskSetting, b: TaskSetting) => {

                        if (a.subheader === 'Coming soon...') {
                            return -1;
                        }
                        else {
                            return a.order === b.order ? 0 : (a.order > b.order ? 1 : -1);
                        }
                    });
                })
            );
    }

    public save(savedSetting: TaskSetting, settings: TaskSetting[]): void {

        savedSetting.disabled = !savedSetting.enabled;

        const settingsToSave = settings.map((setting: TaskSetting) => {

            return {
                id: setting.id,
                name: setting.name,
                icon: setting.icon,
                title: setting.title,
                subheader: setting.subheader,
                task_type: setting.task_type,
                disabled: !setting.enabled
            };
        });

        return this.tasksService.saveSettings(settingsToSave);
    }
}
